import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Check, ChevronRight, LayoutGrid, Lock, Search, X as XIcon } from "lucide-react";
import {
  getHospitalAssistant,
  peekHospitalAssistantCache,
} from "../../api/hospitals";
import { formatFillRate } from "../../utils/hospitalKind";
import {
  accountRoleLabel,
  isMasterAccount,
  type HospitalAccessRole,
} from "../../utils/hospitalAccessRole";
import {
  isClinicNetworkBrandOrSite,
  isClinicNetworkRoot,
  isClinicNetworkSite,
} from "../../utils/clinicNetworkSites";
import {
  readSwitcherView,
  writeSwitcherView,
} from "../../utils/hospitalSwitcherView";
import {
  getHospitalUnreadCount,
  subscribeHospitalUnreadCounts,
} from "../../realtime/notificationCountsStore";

export type { HospitalAccessRole };

export type HospitalAssistantStat = {
  hospitalId?: string;
  accountId?: string;
  id?: string;
  openWithApplicantsCount?: number;
  openWithoutApplicantsCount?: number;
  openCount?: number;
  fillRate?: number | null;
  pendingApplicationsCount?: number;
};

export type HospitalSwitcherRow = {
  id: string;
  name: string;
  parentId?: string | null;
  slave?: boolean;
  inHierarchy?: boolean;
  clinicNetworkRole?: string | null;
};

const statFor = (
  stats: HospitalAssistantStat[],
  hospitalId: string,
): HospitalAssistantStat | undefined =>
  stats.find(
    (row) =>
      String(row.hospitalId || row.accountId || row.id) === String(hospitalId),
  );

const openCount = (stat?: HospitalAssistantStat) => {
  if (!stat) return 0;
  const withApps = Number(stat.openWithApplicantsCount) || 0;
  const without = Number(stat.openWithoutApplicantsCount) || 0;
  const explicit = Number(stat.openCount);
  if (Number.isFinite(explicit) && explicit > 0) return explicit;
  return withApps + without;
};

const applicationsCount = (stat?: HospitalAssistantStat) => {
  if (!stat) return 0;
  const pending = Number(stat.pendingApplicationsCount);
  const withApps = Number(stat.openWithApplicantsCount);
  const pendingCount = Number.isFinite(pending) ? pending : 0;
  const withAppsCount = Number.isFinite(withApps) ? withApps : 0;
  return Math.max(pendingCount, withAppsCount);
};

const descendantsOf = (
  parentId: string,
  hospitals: HospitalSwitcherRow[],
): HospitalSwitcherRow[] => {
  const ids = new Set([parentId]);
  const found: HospitalSwitcherRow[] = [];
  let grew = true;
  while (grew) {
    grew = false;
    hospitals.forEach((hospital) => {
      if (!hospital.parentId || ids.has(hospital.id)) return;
      if (!ids.has(hospital.parentId)) return;
      ids.add(hospital.id);
      found.push(hospital);
      grew = true;
    });
  }
  return found;
};

const matchesQuery = (name: string, query: string) => {
  if (!query) return true;
  const haystack = String(name || "").toLowerCase();
  if (haystack.includes(query)) return true;
  return query
    .split(/\s+/)
    .filter(Boolean)
    .every((part) => haystack.includes(part));
};

export function switcherRootOf<T extends HospitalSwitcherRow>(hospitals: T[]): T | undefined {
  if (!hospitals.length) return undefined;
  try {
    const raw = localStorage.getItem("persistent-parent-hospital");
    if (raw && raw !== "null") {
      const parent = JSON.parse(raw);
      const match = hospitals.find((hospital) => hospital.id && hospital.id === parent?.id);
      if (match) return match;
    }
  } catch {
    // ignore
  }
  return hospitals.find((hospital) => !hospital.parentId) || hospitals[0];
}

const rolledUnread = (
  hospital: HospitalSwitcherRow,
  network: HospitalSwitcherRow[],
) =>
  getHospitalUnreadCount(hospital.id) +
  descendantsOf(hospital.id, network).reduce(
    (sum, child) => sum + getHospitalUnreadCount(child.id),
    0,
  );

const rolledStat = (
  hospital: HospitalSwitcherRow,
  stats: HospitalAssistantStat[],
  network: HospitalSwitcherRow[],
) => {
  const own = statFor(stats, hospital.id);
  const children = descendantsOf(hospital.id, network);
  return {
    unread: rolledUnread(hospital, network),
    apps:
      applicationsCount(own) +
      children.reduce((sum, child) => sum + applicationsCount(statFor(stats, child.id)), 0),
    open:
      openCount(own) +
      children.reduce((sum, child) => sum + openCount(statFor(stats, child.id)), 0),
  };
};

const attentionScore = (
  hospital: HospitalSwitcherRow,
  stats: HospitalAssistantStat[],
  network: HospitalSwitcherRow[],
) => {
  const rolled = rolledStat(hospital, stats, network);
  return rolled.unread * 1_000_000 + rolled.apps * 1_000 + rolled.open;
};

const sortByAttention = (
  left: HospitalSwitcherRow,
  right: HospitalSwitcherRow,
  stats: HospitalAssistantStat[],
  network: HospitalSwitcherRow[],
) => {
  const score =
    attentionScore(right, stats, network) - attentionScore(left, stats, network);
  if (score !== 0) return score;
  return String(left.name || "").localeCompare(String(right.name || ""), undefined, {
    sensitivity: "base",
    numeric: true,
  });
};

const sortByIds = (items: HospitalSwitcherRow[], orderIds: string[]) => {
  const rank = new Map(orderIds.map((id, index) => [id, index]));
  return [...items].sort((left, right) => {
    const leftRank = rank.has(left.id) ? (rank.get(left.id) as number) : 10_000;
    const rightRank = rank.has(right.id) ? (rank.get(right.id) as number) : 10_000;
    if (leftRank !== rightRank) return leftRank - rightRank;
    return String(left.name || "").localeCompare(String(right.name || ""), undefined, {
      sensitivity: "base",
      numeric: true,
    });
  });
};

const pinThenOrder = (
  items: HospitalSwitcherRow[],
  orderIds: string[],
  stats: HospitalAssistantStat[],
  network: HospitalSwitcherRow[],
) => {
  const hot = items.filter((item) => attentionScore(item, stats, network) > 0);
  const rest = items.filter((item) => attentionScore(item, stats, network) <= 0);
  return sortByIds(hot, orderIds).concat(sortByIds(rest, orderIds));
};

const sameAssistantData = (
  left: HospitalAssistantStat[],
  right: HospitalAssistantStat[],
) => JSON.stringify(left) === JSON.stringify(right);

const STATS_COLS = "grid-cols-[3.6rem_2.6rem_2.8rem]";

const HospitalStats = ({ stat }: { stat?: HospitalAssistantStat }) => {
  const open = openCount(stat);
  const apps = applicationsCount(stat);
  return (
    <span className={`grid ${STATS_COLS} items-center text-right font-mono text-[12px] tabular-nums text-[#101319]`}>
      <span>{formatFillRate(stat?.fillRate, open)}</span>
      <span>{open}</span>
      <span className={apps > 0 ? "font-semibold text-[#023eff]" : ""} title="Pending applications">
        {apps}
      </span>
    </span>
  );
};

const SectionHeading = ({ title }: { title: string }) => (
  <div className="flex items-center justify-between gap-3 px-2 pb-1.5 pt-2">
    <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-[#8b93a7]">
      {title}
    </p>
    <span
      className={`grid ${STATS_COLS} text-right text-[10px] font-medium uppercase tracking-[0.06em] text-[#8b93a7]`}
      title="Fill rate · open shifts · pending applications"
    >
      <span>Fill</span>
      <span>Open</span>
      <span>Apps</span>
    </span>
  </div>
);

const SwitcherRow = ({
  hospital,
  subtitle,
  selected,
  showStats,
  stat,
  icon,
  unread,
  indented,
  badge,
  disabled,
  expandable,
  expanded,
  onToggleExpand,
  onClick,
}: {
  hospital: HospitalSwitcherRow;
  subtitle: string;
  selected: boolean;
  showStats?: boolean;
  stat?: HospitalAssistantStat;
  icon?: ReactNode;
  unread?: number;
  indented?: boolean;
  badge?: string;
  disabled?: boolean;
  expandable?: boolean;
  expanded?: boolean;
  onToggleExpand?: () => void;
  onClick?: () => void;
}) => (
  <div
    data-selected={selected ? "true" : undefined}
    className={`grid w-full grid-cols-[1fr_auto] items-center gap-2 rounded-xl px-2 py-2 ${
      indented ? "ml-3 border-l border-[#e6e9f0] pl-3" : ""
    } ${
      selected
        ? "bg-[#e8eeff] shadow-[inset_3px_0_0_#023eff]"
        : disabled
          ? "bg-[#f7f8fb]"
          : "hover:bg-[#f7f8fb]"
    }`}
  >
    <span className="flex min-w-0 items-center gap-2">
      {expandable ? (
        <button
          type="button"
          aria-label={expanded ? "Collapse hospitals" : "Show hospitals"}
          onClick={onToggleExpand}
          className="grid h-7 w-7 shrink-0 place-items-center rounded-lg text-[#8b93a7] hover:bg-white hover:text-[#101319]"
        >
          <ChevronRight
            className={`h-4 w-4 transition-transform ${expanded ? "rotate-90" : ""}`}
          />
        </button>
      ) : icon ? (
        <span className="grid h-7 w-7 shrink-0 place-items-center">{icon}</span>
      ) : (
        <span className="h-7 w-7 shrink-0" />
      )}
      <button
        type="button"
        disabled={disabled || !onClick}
        onClick={onClick}
        className="min-w-0 flex-1 text-left disabled:cursor-default"
      >
        <span className="flex min-w-0 items-center gap-1.5">
          <span className={`truncate text-[13px] font-semibold ${selected ? "text-[#023eff]" : "text-[#101319]"}`}>
            {hospital.name}
          </span>
          {badge ? (
            <span className="shrink-0 rounded-full bg-[#eef1f7] px-1.5 text-[10px] font-semibold leading-4 text-[#5b6475]">
              {badge}
            </span>
          ) : null}
          {unread ? (
            <span className="shrink-0 rounded-full bg-[#023eff] px-1.5 text-[10px] font-semibold leading-4 text-white">
              {unread > 99 ? "99+" : unread}
            </span>
          ) : null}
        </span>
        <span className="block truncate text-[11px] text-[#6b7280]">{subtitle}</span>
      </button>
    </span>
    <span className="flex items-center gap-1.5">
      {showStats ? <HospitalStats stat={stat} /> : null}
      {selected ? <Check className="h-4 w-4 shrink-0 text-[#023eff]" /> : <span className="h-4 w-4 shrink-0" />}
    </span>
  </div>
);

const masterCopy = (
  switchableCount: number,
  siteCount: number,
  network: boolean,
) => {
  if (siteCount > 0 && switchableCount > 0) {
    return {
      subtitle: `${switchableCount} hospital${switchableCount === 1 ? "" : "s"} · ${siteCount} clinic sites`,
      badge: "Clinic network",
    };
  }
  if (siteCount > 0 || network) {
    return {
      subtitle:
        siteCount > 0
          ? `${siteCount} clinic site${siteCount === 1 ? "" : "s"} · not listed here`
          : "Clinic network",
      badge: "Clinic network",
    };
  }
  return {
    subtitle: `${switchableCount} hospital${switchableCount === 1 ? "" : "s"}`,
    badge: "Master",
  };
};

const HospitalSwitcherMenu = ({
  hospitals,
  networkHospitals,
  currentHospitalId,
  searchTerm,
  onSearchTermChange,
  onSelect,
  accessRole = "hospital",
}: {
  hospitals: HospitalSwitcherRow[];
  networkHospitals?: HospitalSwitcherRow[];
  currentHospitalId?: string | null;
  searchTerm: string;
  onSearchTermChange: (value: string) => void;
  onSelect: (hospital: HospitalSwitcherRow) => void;
  accessRole?: HospitalAccessRole;
}) => {
  const savedView = readSwitcherView();
  const fullNetwork = networkHospitals && networkHospitals.length > 0
    ? networkHospitals
    : hospitals;
  const [assistantData, setAssistantData] = useState<HospitalAssistantStat[]>(
    () => peekHospitalAssistantCache() || [],
  );
  const [unreadTick, setUnreadTick] = useState(0);
  const [listReady, setListReady] = useState(false);
  const [expandedMasterIds, setExpandedMasterIds] = useState<string[]>(
    () => savedView?.expandedMasterIds || [],
  );
  const orderRef = useRef<string[]>([]);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const didScrollSelected = useRef(false);
  const didSeedExpand = useRef(
    Boolean(savedView?.selectedId) &&
      String(savedView?.selectedId) === String(currentHospitalId),
  );

  useEffect(() => {
    let cancelled = false;
    getHospitalAssistant()
      .then((data) => {
        if (cancelled) return;
        const next = Array.isArray(data) ? data : [];
        setAssistantData((current) => (sameAssistantData(current, next) ? current : next));
      })
      .finally(() => {
        if (!cancelled) setListReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return subscribeHospitalUnreadCounts(() => {
      setUnreadTick((current) => current + 1);
    });
  }, []);

  useEffect(() => {
    writeSwitcherView({
      expandedMasterIds,
      selectedId: currentHospitalId,
    });
  }, [currentHospitalId, expandedMasterIds]);

  const query = searchTerm.trim().toLowerCase();
  const root = switcherRootOf(hospitals);
  const grouped = useMemo(() => {
    const empty = {
      overview: null as HospitalSwitcherRow | null,
      masters: [] as HospitalSwitcherRow[],
      hospitals: [] as HospitalSwitcherRow[],
    };
    if (!root) return empty;

    if (accessRole === "hospital") {
      const rows = hospitals
        .filter((hospital) => matchesQuery(hospital.name, query))
        .sort((left, right) => sortByAttention(left, right, assistantData, fullNetwork));
      return {
        ...empty,
        hospitals: query || orderRef.current.length === 0
          ? rows
          : pinThenOrder(rows, orderRef.current, assistantData, fullNetwork),
      };
    }

    const masters =
      accessRole === "grandmaster"
        ? hospitals.filter((hospital) =>
            isMasterAccount(hospital, root.id, hospitals),
          )
        : [];
    const nestedIds = new Set<string>();
    masters.forEach((master) => {
      descendantsOf(master.id, hospitals).forEach((child) => nestedIds.add(child.id));
    });
    const ungrouped = hospitals.filter(
      (hospital) =>
        hospital.id !== root.id &&
        !masters.some((master) => master.id === hospital.id) &&
        !nestedIds.has(hospital.id),
    );

    const nextMasters = masters
      .filter(
        (master) =>
          matchesQuery(master.name, query) ||
          descendantsOf(master.id, fullNetwork).some((child) =>
            matchesQuery(child.name, query),
          ),
      )
      .sort((left, right) => sortByAttention(left, right, assistantData, fullNetwork));
    const searchableHospitals = query
      ? hospitals.filter(
          (hospital) =>
            hospital.id !== root.id &&
            !masters.some((master) => master.id === hospital.id) &&
            matchesQuery(hospital.name, query),
        )
      : ungrouped;
    const nextHospitals = searchableHospitals
      .filter((hospital) => (query ? true : matchesQuery(hospital.name, query)))
      .sort((left, right) => sortByAttention(left, right, assistantData, fullNetwork));

    if (!query && orderRef.current.length > 0) {
      return {
        overview: matchesQuery(root.name, query) ? root : null,
        masters: pinThenOrder(nextMasters, orderRef.current, assistantData, fullNetwork),
        hospitals: pinThenOrder(nextHospitals, orderRef.current, assistantData, fullNetwork),
      };
    }

    return {
      overview: matchesQuery(root.name, query) ? root : null,
      masters: nextMasters,
      hospitals: nextHospitals,
    };
  }, [accessRole, assistantData, fullNetwork, hospitals, query, root, unreadTick]);

  useEffect(() => {
    if (query || !listReady || orderRef.current.length > 0) return;
    const next = grouped.masters
      .map((master) => master.id)
      .concat(grouped.hospitals.map((hospital) => hospital.id));
    if (next.length === 0) return;
    orderRef.current = next;
  }, [grouped.hospitals, grouped.masters, listReady, query]);

  useEffect(() => {
    if (!query) return;
    const matchedMasterIds = grouped.masters
      .filter((master) =>
        descendantsOf(master.id, fullNetwork).some((child) =>
          matchesQuery(child.name, query),
        ),
      )
      .map((master) => master.id);
    if (!matchedMasterIds.length) return;
    setExpandedMasterIds((current) => {
      const extras = matchedMasterIds.filter((id) => current.indexOf(id) === -1);
      return extras.length === 0 ? current : current.concat(extras);
    });
  }, [fullNetwork, grouped.masters, query]);

  useEffect(() => {
    if (!root || didSeedExpand.current) return;
    didSeedExpand.current = true;
    const next: string[] = [];
    let cursor = hospitals.find(
      (hospital) => String(hospital.id) === String(currentHospitalId),
    );
    while (cursor?.parentId) {
      next.push(cursor.parentId);
      const parentId = cursor.parentId;
      cursor = hospitals.find((hospital) => hospital.id === parentId);
    }
    if (next.length === 0) return;
    setExpandedMasterIds((current) => {
      const extras = next.filter((id) => current.indexOf(id) === -1);
      return extras.length === 0 ? current : current.concat(extras);
    });
  }, [currentHospitalId, hospitals, root]);

  useEffect(() => {
    if (didScrollSelected.current) return;
    const frame = window.requestAnimationFrame(() => {
      const selected = scrollRef.current?.querySelector('[data-selected="true"]');
      if (selected && typeof selected.scrollIntoView === "function") {
        selected.scrollIntoView({ block: "center", inline: "nearest" });
        didScrollSelected.current = true;
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [grouped]);

  const selectedHospital =
    hospitals.find((hospital) => String(hospital.id) === String(currentHospitalId)) ||
    fullNetwork.find((hospital) => String(hospital.id) === String(currentHospitalId)) ||
    null;
  const selectedRole = accountRoleLabel(
    selectedHospital,
    accessRole,
    root?.id,
    hospitals,
  );
  const hospitalCount = Math.max(hospitals.length - (root ? 1 : 0), 0);
  const footer =
    accessRole === "grandmaster"
      ? "You can switch any hospital in this organisation. A hospital login only sees its own hospital."
      : accessRole === "master"
        ? "You can switch any hospital under this master. A hospital login only sees its own hospital."
        : hospitals.length > 1
          ? "You can switch the hospitals you have access to."
          : "";

  const empty =
    !grouped.overview &&
    grouped.masters.length === 0 &&
    grouped.hospitals.length === 0;

  const toggleMaster = (masterId: string) => {
    setExpandedMasterIds((current) => {
      const next = current.includes(masterId)
        ? current.filter((id) => id !== masterId)
        : current.concat([masterId]);
      writeSwitcherView({ expandedMasterIds: next });
      return next;
    });
  };

  return (
    <div className="flex w-[460px] max-h-[min(72vh,640px)] flex-col overflow-hidden rounded-2xl border border-[#e6e9f0] bg-white shadow-[0_24px_48px_-24px_rgba(16,19,25,0.28)]">
      <div className="shrink-0 p-3 pb-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8b93a7]" />
          <input
            type="text"
            value={searchTerm}
            onChange={(event) => onSearchTermChange(event.target.value)}
            placeholder="Find a hospital"
            className="h-11 w-full rounded-xl border-0 bg-[#f4f6fa] pl-10 pr-10 text-[14px] text-[#101319] outline-none placeholder:text-[#8b93a7] focus:bg-[#eef1f7]"
            autoFocus
          />
          {searchTerm ? (
            <button
              type="button"
              onClick={() => onSearchTermChange("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8b93a7] hover:text-[#101319]"
            >
              <XIcon className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>
      </div>

      {selectedHospital ? (
        <div className="mx-3 mb-2 shrink-0 rounded-xl bg-[#023eff] px-3 py-2.5 text-white">
          <p className="m-0 text-[10px] font-semibold uppercase tracking-[0.14em] text-white/70">
            Currently viewing
          </p>
          <div className="mt-0.5 flex items-center justify-between gap-3">
            <p className="m-0 truncate text-[14px] font-semibold">{selectedHospital.name}</p>
            <span className="shrink-0 rounded-full bg-white/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em]">
              {selectedRole}
            </span>
          </div>
        </div>
      ) : null}

      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto px-3 pb-2">
        {grouped.overview ? (
          <div className="mb-2">
            <p className="px-2 pb-1.5 text-[11px] font-medium uppercase tracking-[0.14em] text-[#8b93a7]">
              {accessRole === "grandmaster" ? "Grandmaster" : "Organisation"}
            </p>
            <button
              type="button"
              data-selected={
                String(currentHospitalId) === String(grouped.overview.id) ? "true" : undefined
              }
              onClick={() => onSelect(grouped.overview!)}
              className={`flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left ${
                String(currentHospitalId) === String(grouped.overview.id)
                  ? "bg-[#e8eeff] shadow-[inset_3px_0_0_#023eff]"
                  : "bg-[#f4f6fa] hover:bg-[#eef1f7]"
              }`}
            >
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white text-[#023eff] shadow-[0_0_0_1px_#e6e9f0]">
                <LayoutGrid className="h-4 w-4" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[15px] font-semibold text-[#101319]">
                  {grouped.overview.name}
                </span>
                <span className="block text-[12px] text-[#6b7280]">
                  {accessRole === "grandmaster" ? "Grandmaster" : "Master"}
                  {" · "}
                  {hospitalCount} hospital{hospitalCount === 1 ? "" : "s"}
                </span>
              </span>
              {String(currentHospitalId) === String(grouped.overview.id) ? (
                <Check className="h-4 w-4 shrink-0 text-[#023eff]" />
              ) : null}
            </button>
          </div>
        ) : null}

        {grouped.masters.length > 0 ? (
          <div className="pb-1">
            <SectionHeading title="Masters" />
            {grouped.masters.map((master) => {
              const switchableIds = new Set(hospitals.map((hospital) => hospital.id));
              const switchable = descendantsOf(master.id, hospitals).filter(
                (child) =>
                  !query ||
                  matchesQuery(child.name, query) ||
                  matchesQuery(master.name, query),
              );
              const orderedSwitchable =
                query || orderRef.current.length === 0
                  ? [...switchable].sort((left, right) =>
                      sortByAttention(left, right, assistantData, fullNetwork),
                    )
                  : pinThenOrder(switchable, orderRef.current, assistantData, fullNetwork);
              const networkChildren = descendantsOf(master.id, fullNetwork);
              const hidden = networkChildren.filter((child) => !switchableIds.has(child.id));
              const clinicSites = hidden.filter((child) => isClinicNetworkSite(child));
              const clinicBrands = hidden.filter(
                (child) =>
                  isClinicNetworkBrandOrSite(child) && !isClinicNetworkSite(child),
              );
              const hiddenCount = clinicSites.length || hidden.length;
              const network = isClinicNetworkRoot(master) || hidden.length > 0;
              const copy = masterCopy(orderedSwitchable.length, hiddenCount, network);
              const expanded = expandedMasterIds.includes(master.id);
              const expandable =
                orderedSwitchable.length > 0 || clinicBrands.length > 0 || hiddenCount > 0;
              const childUnread = orderedSwitchable.reduce(
                (sum, child) => sum + getHospitalUnreadCount(child.id),
                0,
              );
              const childApps = orderedSwitchable.reduce(
                (sum, child) => sum + applicationsCount(statFor(assistantData, child.id)),
                0,
              );
              const childOpen = orderedSwitchable.reduce(
                (sum, child) => sum + openCount(statFor(assistantData, child.id)),
                0,
              );
              const masterStat = statFor(assistantData, master.id);
              return (
                <div key={master.id}>
                  <SwitcherRow
                    hospital={master}
                    subtitle={copy.subtitle}
                    badge={copy.badge}
                    selected={String(currentHospitalId) === String(master.id)}
                    unread={childUnread}
                    showStats
                    expandable={expandable}
                    expanded={expanded}
                    onToggleExpand={() => toggleMaster(master.id)}
                    stat={{
                      openCount: childOpen + openCount(masterStat),
                      pendingApplicationsCount:
                        childApps + applicationsCount(masterStat),
                      fillRate: masterStat?.fillRate ?? null,
                      openWithApplicantsCount: 0,
                      openWithoutApplicantsCount: 0,
                    }}
                    onClick={() => onSelect(master)}
                  />
                  {expanded && !query ? (
                    <div className="pb-1">
                      {clinicBrands.map((brand) => {
                        const siteCount = descendantsOf(brand.id, fullNetwork).filter(
                          (child) => isClinicNetworkSite(child),
                        ).length;
                        return (
                          <SwitcherRow
                            key={brand.id}
                            hospital={brand}
                            subtitle={
                              siteCount > 0
                                ? `${siteCount} clinic sites · not switchable here`
                                : "Clinic network · not switchable here"
                            }
                            badge="Clinic network"
                            selected={false}
                            indented
                            disabled
                          />
                        );
                      })}
                      {orderedSwitchable.length === 0 && clinicBrands.length === 0 && hiddenCount > 0 ? (
                        <SwitcherRow
                          hospital={master}
                          subtitle={`${hiddenCount} clinic sites · not switchable here`}
                          badge="Clinic network"
                          selected={false}
                          indented
                          disabled
                        />
                      ) : orderedSwitchable.length === 0 && clinicBrands.length === 0 ? (
                        <p className="px-8 py-2 text-[12px] text-[#8b93a7]">
                          No hospitals under this master.
                        </p>
                      ) : (
                        orderedSwitchable.map((child) => (
                          <SwitcherRow
                            key={child.id}
                            hospital={child}
                            subtitle="Hospital"
                            selected={String(currentHospitalId) === String(child.id)}
                            showStats
                            stat={statFor(assistantData, child.id)}
                            unread={getHospitalUnreadCount(child.id)}
                            indented
                            onClick={() => onSelect(child)}
                          />
                        ))
                      )}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : null}

        <div>
          <SectionHeading
            title={
              query
                ? "Matching hospitals"
                : accessRole === "grandmaster"
                  ? "Other hospitals"
                  : "Hospitals"
            }
          />
          {empty ? (
            <p className="px-1 py-6 text-center text-[13px] text-[#8b93a7]">
              {query ? "No hospitals found" : "No hospitals available"}
            </p>
          ) : grouped.hospitals.length === 0 ? (
            <p className="px-1 py-3 text-[12px] text-[#8b93a7]">
              {accessRole === "grandmaster"
                ? "Hospitals under a master are listed inside that master."
                : "No hospitals to show."}
            </p>
          ) : (
            grouped.hospitals.map((hospital) => {
              const switchableIds = new Set(hospitals.map((row) => row.id));
              const networkChildren = descendantsOf(hospital.id, fullNetwork);
              const hidden = networkChildren.filter((child) => !switchableIds.has(child.id));
              const siteCount =
                hidden.filter((child) => isClinicNetworkSite(child)).length ||
                hidden.length;
              const network = isClinicNetworkRoot(hospital) || hidden.length > 0;
              return (
                <SwitcherRow
                  key={hospital.id}
                  hospital={hospital}
                  subtitle={
                    network
                      ? siteCount > 0
                        ? `Clinic network · ${siteCount} sites`
                        : "Clinic network"
                      : "Hospital"
                  }
                  badge={network ? "Clinic network" : undefined}
                  selected={String(currentHospitalId) === String(hospital.id)}
                  showStats
                  stat={statFor(assistantData, hospital.id)}
                  unread={getHospitalUnreadCount(hospital.id)}
                  onClick={() => onSelect(hospital)}
                />
              );
            })
          )}
        </div>
      </div>

      <div className="shrink-0 border-t border-[#eef1f6] px-4 py-2.5">
        <p className="m-0 text-[11px] leading-4 text-[#8b93a7]">
          <span className="font-semibold text-[#5b6475]">Apps</span>
          {" = pending applications on open shifts. Sorted by updates, then applications, then open."}
        </p>
        {footer ? (
          <div className="mt-1.5 flex items-start gap-2 text-[11px] leading-4 text-[#6b7280]">
            <Lock className="mt-0.5 h-3 w-3 shrink-0 text-[#8b93a7]" />
            <p className="m-0">{footer}</p>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default HospitalSwitcherMenu;
